import timezoneToCountry from "@/common/utilities/locale/timezoneToCountry";

/**
 * The country the visitor's machine is physically in, from its IANA time zone.
 *
 * This is the stronger of the two signals: a time zone is set from where the
 * device is, whereas a language is set from what the user wants to read.
 */
const countryFromTimeZone = (): string | undefined => {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return timeZone ? timezoneToCountry[timeZone] : undefined;
    } catch {
        // Intl is present in every browser we support, but a locked-down or
        // unusual environment is not worth throwing over -- fall through to the
        // language signal.
        return undefined;
    }
};

/**
 * The region subtag of the visitor's preferred languages: "en-US" -> "US".
 *
 * Weaker than the time zone, because plenty of people outside the US read the
 * web in en-US. Only consulted when the time zone gives us nothing.
 */
const countryFromLanguages = (): string | undefined => {
    const tags = navigator.languages.length
        ? navigator.languages
        : [navigator.language];

    for (const tag of tags) {
        if (!tag) continue;
        try {
            const region = new Intl.Locale(tag).region;
            // A region subtag can also be a UN M49 area code ("es-419" is Latin
            // America), which is not a country and cannot seed a phone number.
            if (region && /^[A-Z]{2}$/.test(region)) return region;
        } catch {
            // Malformed tag; the next one may still be usable.
        }
    }

    return undefined;
};

/**
 * The visitor's most likely country as an ISO 3166-1 alpha-2 code, or undefined
 * when the browser offers nothing usable.
 *
 * Returns undefined on the server, which has no visitor locale to read: callers
 * rendered on both sides must not read this during render, or the markup will
 * disagree with the client's. Use useBrowserCountry, which resolves after mount.
 */
export const getBrowserCountry = (): string | undefined => {
    if (typeof window === "undefined") return undefined;
    return countryFromTimeZone() ?? countryFromLanguages();
};
